import React, { useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import DashboardDataTable from './DashboardDataTable';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';

const TITLES = {
  hosts: 'Hosts',
  growth: 'New hosts',
  pms: 'PMS distribution',
  drivers: 'Drivers',
  orders: 'Orders',
  properties: 'Properties',
};

function buildHostColumns() {
  return [
    {
      key: 'name',
      header: 'Host',
      searchable: true,
      sortable: true,
      accessor: (r) => r.name,
      cell: (r) => (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
            {(r.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-zinc-900">{r.name}</p>
            <p className="truncate text-[10px] text-zinc-500">{r.email || '—'}</p>
          </div>
        </div>
      ),
    },
    { key: 'pmsType', header: 'PMS', sortable: true, accessor: (r) => PMS_LABELS[r.pmsType] || '—' },
    { key: 'propertyCount', header: 'Props', align: 'right', sortable: true, accessor: (r) => r.propertyCount },
    { key: 'orderCount', header: 'Orders', align: 'right', sortable: true, accessor: (r) => r.orderCount || 0 },
    {
      key: 'aov',
      header: 'AOV',
      align: 'right',
      sortable: true,
      accessor: (r) => r.aov || 0,
      cell: (r) => fmtMoney(r.aov || 0),
    },
    {
      key: 'revenue',
      header: 'Volume',
      align: 'right',
      sortable: true,
      accessor: (r) => r.revenue || 0,
      cell: (r) => fmtMoney(r.revenue || 0),
    },
    {
      key: 'isNew',
      header: 'Type',
      sortable: true,
      accessor: (r) => (r.isNew ? 'New' : 'Legacy'),
      cell: (r) => (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
            r.isNew ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
          }`}
        >
          {r.isNew ? 'New' : 'Legacy'}
        </span>
      ),
    },
  ];
}

function buildOrderColumns() {
  return [
    {
      key: 'uniqueId',
      header: 'Order',
      searchable: true,
      sortable: true,
      accessor: (r) => r.uniqueId || r._id,
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">
            {r.uniqueId || String(r._id).slice(-6)}
          </p>
          <p className="truncate text-[10px] text-zinc-500">
            {r?.property?.address?.line1 || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
      cell: (r) =>
        r.createdAt
          ? new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      cell: (r) => (
        <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700">
          {r.status}
        </span>
      ),
    },
    {
      key: 'storeLocation',
      header: 'Store',
      searchable: true,
      sortable: true,
      accessor: (r) => r.storeLocation || '',
    },
    {
      key: 'revenue',
      header: 'Revenue',
      align: 'right',
      sortable: true,
      sortAccessor: (r) => r._revenue || 0,
      accessor: (r) => r._revenue || 0,
      cell: (r) => fmtMoney(r._revenue || 0),
    },
  ];
}

function buildDriverColumns() {
  return [
    {
      key: 'name',
      header: 'Driver',
      searchable: true,
      sortable: true,
      accessor: (r) => r.name,
      cell: (r) => (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-50 text-[10px] font-semibold text-emerald-700">
            {(r.name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-zinc-900">{r.name}</p>
            <p className="truncate text-[10px] text-zinc-500">{r.email || '—'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Acquired',
      sortable: true,
      sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
      cell: (r) =>
        r.createdAt
          ? new Date(r.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    { key: 'market', header: 'Market', sortable: true, searchable: true, accessor: (r) => r.market || '—' },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      accessor: (r) => (r.active ? 'Active' : 'Inactive'),
      cell: (r) => (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
            r.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];
}

function buildPropertyColumns() {
  return [
    {
      key: 'address',
      header: 'Address',
      searchable: true,
      sortable: true,
      accessor: (r) => `${r?.address?.line1 || ''} ${r?.address?.city || ''}`,
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">
            {r?.address?.line1 || r?.listingTitle || 'Property'}
          </p>
          <p className="truncate text-[10px] text-zinc-500">
            {r?.address?.city || ''}{r?.address?.state ? `, ${r.address.state}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      sortAccessor: (r) => new Date(r.createdAt || 0).getTime(),
      cell: (r) =>
        r.createdAt
          ? new Date(r.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      key: 'propertySource',
      header: 'Source',
      sortable: true,
      searchable: true,
      accessor: (r) => PMS_LABELS[r.propertySource] || r.propertySource || '—',
    },
  ];
}

function buildPmsColumns(rows, propertiesByPmsAvailable) {
  return [
    {
      key: 'label',
      header: 'PMS',
      searchable: true,
      sortable: true,
      cell: (r) => <span className="font-semibold text-zinc-900">{r.label}</span>,
    },
    { key: 'count', header: 'Hosts', align: 'right', sortable: true, cell: (r) => fmtNumber(r.count) },
    {
      key: 'properties',
      header: 'Properties',
      align: 'right',
      sortable: true,
      cell: (r) => (propertiesByPmsAvailable ? fmtNumber(r.properties) : '—'),
    },
    {
      key: 'share',
      header: 'Host share',
      align: 'right',
      sortable: true,
      sortAccessor: (r) => r.count,
      cell: (r) => {
        const total = rows.reduce((s, x) => s + x.count, 0);
        return total ? `${Math.round((r.count / total) * 100)}%` : '0%';
      },
    },
  ];
}

// Slide-up bottom sheet that hosts the existing reusable DashboardDataTable.
// The exact column set, row source and click handler are derived from the
// currently active canvas view. Closes on ESC, backdrop click and the X.
export default function RecordsSheet({
  open,
  view,
  onClose,
  hostRows = [],
  filteredOrders = [],
  drivers = [],
  properties = [],
  filteredHosts = [],
  propertiesByPms,
  onSelectHost,
  onSelectOrder,
  onSelectProperty,
  onSelectPms,
}) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const config = useMemo(() => {
    if (view === 'hosts' || view === 'growth') {
      return {
        title: TITLES[view],
        rows: hostRows,
        columns: buildHostColumns(),
        onRowClick: onSelectHost,
        searchPlaceholder: 'Search hosts by name, email, company…',
        initialSort: view === 'growth' ? { key: 'createdAt', dir: 'desc' } : { key: 'orderCount', dir: 'desc' },
      };
    }
    if (view === 'orders') {
      return {
        title: TITLES.orders,
        rows: filteredOrders,
        columns: buildOrderColumns(),
        onRowClick: onSelectOrder,
        searchPlaceholder: 'Search orders by id, store, status…',
        initialSort: { key: 'createdAt', dir: 'desc' },
      };
    }
    if (view === 'drivers') {
      return {
        title: TITLES.drivers,
        rows: drivers,
        columns: buildDriverColumns(),
        searchPlaceholder: 'Search drivers by name, email, market…',
        initialSort: { key: 'createdAt', dir: 'desc' },
      };
    }
    if (view === 'properties') {
      return {
        title: TITLES.properties,
        rows: properties,
        columns: buildPropertyColumns(),
        onRowClick: onSelectProperty,
        searchPlaceholder: 'Search properties by address, city…',
        initialSort: { key: 'createdAt', dir: 'desc' },
      };
    }
    if (view === 'pms') {
      const m = new Map();
      filteredHosts.forEach((h) => {
        const k = h.pmsType || 'unknown';
        m.set(k, (m.get(k) || 0) + 1);
      });
      const rows = Array.from(m.entries())
        .map(([k, count]) => ({
          id: k,
          label: PMS_LABELS[k] || k,
          count,
          properties: propertiesByPms?.get(k) || 0,
        }))
        .sort((a, b) => b.count - a.count);
      const propertiesByPmsAvailable = Boolean(propertiesByPms && propertiesByPms.size > 0);
      return {
        title: TITLES.pms,
        rows,
        columns: buildPmsColumns(rows, propertiesByPmsAvailable),
        onRowClick: (d) => onSelectPms?.(d.id),
        searchPlaceholder: 'Search PMS…',
        initialSort: { key: propertiesByPmsAvailable ? 'properties' : 'count', dir: 'desc' },
      };
    }
    return null;
  }, [view, hostRows, filteredOrders, drivers, properties, filteredHosts, propertiesByPms, onSelectHost, onSelectOrder, onSelectProperty, onSelectPms]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-40">
      <div
        className={`absolute inset-0 bg-zinc-900/30 backdrop-blur-[1px] transition-opacity duration-200 motion-reduce:transition-none ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={config?.title || 'Records'}
        className={`absolute inset-x-0 bottom-0 mx-auto flex h-[60vh] max-w-[1600px] flex-col overflow-hidden rounded-t-2xl border border-zinc-200 bg-white shadow-2xl transition-transform duration-200 ease-out motion-reduce:transition-none ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="h-1 w-10 rounded-full bg-zinc-200" aria-hidden />
            <h3 className="text-sm font-semibold text-zinc-900">
              {config?.title || 'Records'}
            </h3>
            {config ? (
              <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-600">
                {fmtNumber(config.rows.length)}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close records"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors duration-150 hover:bg-zinc-100 hover:text-zinc-900 motion-reduce:transition-none"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden p-3">
          {config ? (
            <DashboardDataTable
              rows={config.rows}
              columns={config.columns}
              onRowClick={config.onRowClick}
              searchPlaceholder={config.searchPlaceholder}
              initialSort={config.initialSort}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-zinc-500">
              No records available for this view.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
